import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../config';
import './PricesPage.css'; // We'll create this for table styling

interface PricePeriod {
    id: number;
    name_key: string;
    date_from: string;
    date_to: string;
    min_nights?: number;
}

interface ApartmentPriceDetail {
    price_period_id: number;
    price_per_night_standard_float: number;
    price_per_night_auxiliary_bed_float: number | null;
}

interface FormattedApartmentPrice {
    apartment_id: number;
    apartment_name: string;
    type?: string;
    period_prices: ApartmentPriceDetail[];
}

interface PricingData {
    pricePeriods: PricePeriod[];
    apartmentPrices: FormattedApartmentPrice[];
}

const PricesPage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const [pricingData, setPricingData] = useState<PricingData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchPricingData = async () => {
            try {
                setLoading(true);
                const response = await fetch(`${API_URL}/api/pricing/all-prices-structured`);
                if (!response.ok) {
                    const errorData = await response.text(); // Get text to see if it's HTML or a plain error message
                    console.error("Error response from server:", errorData);
                    throw new Error(`Failed to fetch pricing data: ${response.statusText} - ${errorData}`);
                }
                const data: PricingData = await response.json();
                setPricingData(data);
                setError(null);
            } catch (err: any) {
                setError(err.message);
                console.error("Error fetching pricing data:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchPricingData();
    }, []);

    const formatDateDisplay = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' });
    };

    if (loading) return <p className="prices-container">{t('loading')}</p>;
    if (error || !pricingData) return <p className="prices-container text-center text-error mt-8">Error: {error || 'Failed to load data'}</p>;

    const { pricePeriods, apartmentPrices } = pricingData;

    const getPeriodStr = (period?: PricePeriod) => period ? `${formatDateDisplay(period.date_from)} — ${formatDateDisplay(period.date_to)}` : 'Dates TBD';
    const getPrice = (ap: FormattedApartmentPrice, periodId?: number) => {
        if (!periodId) return 'N/A';
        const detail = ap.period_prices.find((p) => p.price_period_id === periodId);
        return detail ? `€${detail.price_per_night_standard_float}` : 'N/A';
    };

    const standardPricing = apartmentPrices.find(ap => ap.type === 'standard' || ap.apartment_id === 1);
    const premiumPricing = apartmentPrices.find(ap => ap.type === 'premium' || ap.apartment_id === 5);
    const groupedPrices: FormattedApartmentPrice[] = [];
    if (standardPricing) groupedPrices.push({ ...standardPricing, apartment_name: t('bookingPage.standardTitle') });
    if (premiumPricing) groupedPrices.push({ ...premiumPricing, apartment_name: t('bookingPage.premiumTitle') });

    const peakPeriod = pricePeriods.find((p: PricePeriod) => p.name_key === 'pricesPage.table.periodPeak') || pricePeriods.find((p: PricePeriod) => p.name_key === 'period.season') || pricePeriods[2];
    
    // Group 1: Summer
    const earlySummerPeriod = pricePeriods.find((p: PricePeriod) => p.name_key === 'pricesPage.table.period2');
    const lateSummerPeriod = pricePeriods.find((p: PricePeriod) => p.name_key === 'pricesPage.table.period4');

    // Group 2: Shoulder
    const preSeasonPeriod = pricePeriods.find((p: PricePeriod) => p.name_key === 'pricesPage.table.period1');
    const postSeasonPeriod = pricePeriods.find((p: PricePeriod) => p.name_key === 'pricesPage.table.period5');

    return (
        <div className="bg-surface text-on-surface font-body selection:bg-secondary-container selection:text-on-secondary-container fade-in">
            <main className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-16">
                {/* Hero Header Section */}
                <header className="mb-20 text-center md:text-left max-w-3xl">
                    <span className="font-label uppercase tracking-[0.2em] text-secondary font-bold text-xs mb-4 block">{t('pricesPage.heroLabel')}</span>
                    <h1 className="text-3xl sm:text-5xl md:text-7xl font-light text-primary mb-8 leading-tight">{t('pricesPage.title').split('.')[0]}. <span className="italic font-normal">{t('pricesPage.summerMemories')}</span></h1>
                    <p className="text-on-surface-variant text-lg leading-relaxed font-body">
                        {t('pricesPage.heroSubtitle')}
                    </p>
                </header>

                {/* Pricing Grid Layout */}
                <div className="space-y-32">
                    {/* Peak Season Selection & Layout */}
                    <section className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                        <div className="lg:col-span-4 lg:sticky top-32">
                            <div className="bg-surface-container-low border border-outline-variant/10 rounded-xl p-8 space-y-6">
                                <h3 className="text-2xl font-headline text-primary">{peakPeriod ? t(peakPeriod.name_key) : t('pricesPage.season.peak.title')}</h3>
                                <p className="text-sm text-on-surface-variant leading-relaxed">{t('pricesPage.season.peak.desc')}</p>
                                <div className="pt-4 flex flex-col gap-2 text-secondary">
                                    <div className="flex items-center gap-4">
                                        <span className="material-symbols-outlined material-symbols-rounded">event</span>
                                        <span className="font-label text-xs uppercase tracking-widest font-bold">{getPeriodStr(peakPeriod)}</span>
                                    </div>
                                    {peakPeriod?.min_nights && (
                                        <div className="flex items-center gap-4 text-on-surface-variant">
                                            <span className="material-symbols-outlined material-symbols-rounded">nightlight</span>
                                            <span className="font-label text-[10px] uppercase tracking-widest font-bold">{t('pricesPage.minNights', { count: peakPeriod.min_nights, defaultValue: `Min. ${peakPeriod.min_nights} nights` })}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="mt-8 relative h-64 rounded-xl overflow-hidden shadow-[0_10px_40px_rgba(27,28,25,0.05)] border border-outline-variant/20">
                                <img alt={t('pricesPage.season.peak.alt')} className="w-full h-full object-cover" src="https://lh3.googleusercontent.com/aida-public/AB6AXuBsnh8_Jsw4j15u35EB6WbN68v1g9PWLV-QPOggaDCKRexhCjjgn-BlttxHWxYgFa5js2NEiUvlSaAklksxKra41Pvs-TZmji-hTp0xhINiWaul3t0fZdVzovfiuA42OoP5oX5auzNuWGRNMyUIazqHpQsTIcxQvKl8yeduxQuzcJ7Lc7oEiia6BIXZPCJ-k_AUNOw9i6uxWd6TyKTNHU_BwpfF7Se8BFzGr7MZGRyjpS4HbkRZ9gVf-Esj12jZkEVRbzFfi1TVbo4"/>
                            </div>
                        </div>
                        <div className="lg:col-span-8 overflow-x-auto">
                            <div className="bg-surface-container-lowest rounded-xl overflow-hidden shadow-[0_10px_40px_rgba(27,28,25,0.03)] border border-outline-variant/10">
                                <table className="w-full border-collapse text-left">
                                    <thead className="bg-surface-container-high">
                                        <tr>
                                            <th className="p-4 md:p-8 font-label text-[10px] uppercase tracking-widest text-on-surface-variant">{t('pricesPage.table.header.type')}</th>
                                            <th className="p-4 md:p-8 font-label text-[10px] uppercase tracking-widest text-on-surface-variant">{t('pricesPage.table.header.capacity')}</th>
                                            <th className="p-4 md:p-8 font-label text-[10px] uppercase tracking-widest text-on-surface-variant text-right">{t('pricesPage.table.header.price') || 'PRICE'}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-surface-container border-b border-surface-container">
                                        {groupedPrices.map((ap: FormattedApartmentPrice) => (
                                            <tr key={`peak-${ap.apartment_id}`} className="hover:bg-surface-container-low transition-colors group">
                                                <td className="p-4 md:p-8">
                                                    <div className="font-headline text-xl text-primary">{ap.apartment_name}</div>
                                                    <div className="text-xs text-secondary font-bold uppercase mt-1">{t('pricesPage.table.premiumView') || 'Premium View'}</div>
                                                </td>
                                                <td className="p-4 md:p-8 text-on-surface-variant font-body">{t('pricesPage.table.guests')}</td>
                                                <td className="p-4 md:p-8 text-right font-headline text-xl md:text-3xl text-primary font-bold bg-primary/5">{getPrice(ap, peakPeriod?.id)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </section>
                    {/* Summer Seasons Split Bento */}
                    {(earlySummerPeriod || lateSummerPeriod) && (
                        <section className="grid grid-cols-1 md:grid-cols-2 gap-12">
                            {/* Early Summer */}
                            {earlySummerPeriod && (
                            <div className="space-y-8">
                                <div className="flex items-end justify-between border-b border-surface-container-highest pb-4">
                                    <h2 className="text-3xl font-headline text-primary">{t(earlySummerPeriod.name_key)}</h2>
                                    <div className="flex flex-col items-end">
                                        <span className="font-label text-xs tracking-widest text-secondary font-bold">{getPeriodStr(earlySummerPeriod)}</span>
                                        {earlySummerPeriod?.min_nights && <span className="font-label text-[10px] tracking-widest text-on-surface-variant font-bold mt-1 uppercase">{t('pricesPage.minNights', { count: earlySummerPeriod.min_nights, defaultValue: `Min. ${earlySummerPeriod.min_nights} nights` })}</span>}
                                    </div>
                                </div>
                                <div className="bg-surface-container-low rounded-xl p-1 shadow-[0_10px_40px_rgba(27,28,25,0.02)] border border-outline-variant/10">
                                    {groupedPrices.map((ap: FormattedApartmentPrice, idx: number) => (
                                        <div key={`early-${ap.apartment_id}`} className={`bg-surface-container-lowest rounded-lg p-6 flex justify-between items-center ${idx < groupedPrices.length - 1 ? 'mb-1' : ''}`}>
                                            <div>
                                                <h4 className="font-body font-bold text-primary">{ap.apartment_name}</h4>
                                                <p className="text-xs text-on-surface-variant font-body">{t('pricesPage.table.guests')}</p>
                                            </div>
                                            <span className="font-headline text-2xl text-primary font-bold">{getPrice(ap, earlySummerPeriod?.id)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            )}

                            {/* Late Summer */}
                            {lateSummerPeriod && (
                            <div className="space-y-8">
                                <div className="flex items-end justify-between border-b border-surface-container-highest pb-4">
                                    <h2 className="text-3xl font-headline text-primary">{t(lateSummerPeriod.name_key)}</h2>
                                    <div className="flex flex-col items-end">
                                        <span className="font-label text-xs tracking-widest text-secondary font-bold">{getPeriodStr(lateSummerPeriod)}</span>
                                        {lateSummerPeriod?.min_nights && <span className="font-label text-[10px] tracking-widest text-on-surface-variant font-bold mt-1 uppercase">{t('pricesPage.minNights', { count: lateSummerPeriod.min_nights, defaultValue: `Min. ${lateSummerPeriod.min_nights} nights` })}</span>}
                                    </div>
                                </div>
                                <div className="bg-surface-container-low rounded-xl p-1 shadow-[0_10px_40px_rgba(27,28,25,0.02)] border border-outline-variant/10">
                                    {groupedPrices.map((ap: FormattedApartmentPrice, idx: number) => (
                                        <div key={`late-${ap.apartment_id}`} className={`bg-surface-container-lowest rounded-lg p-6 flex justify-between items-center ${idx < groupedPrices.length - 1 ? 'mb-1' : ''}`}>
                                            <div>
                                                <h4 className="font-body font-bold text-primary">{ap.apartment_name}</h4>
                                                <p className="text-xs text-on-surface-variant font-body">{t('pricesPage.table.guests')}</p>
                                            </div>
                                            <span className="font-headline text-2xl text-primary font-bold">{getPrice(ap, lateSummerPeriod?.id)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            )}
                        </section>
                    )}

                    {/* Shoulder Seasons Split Bento */}
                    <section className="grid grid-cols-1 md:grid-cols-2 gap-12">
                        {/* Pre Season */}
                        {preSeasonPeriod && (
                        <div className="space-y-8">
                            <div className="flex items-end justify-between border-b border-surface-container-highest pb-4">
                                <h2 className="text-3xl font-headline text-primary">{t(preSeasonPeriod.name_key)}</h2>
                                <div className="flex flex-col items-end">
                                    <span className="font-label text-xs tracking-widest text-secondary font-bold">{getPeriodStr(preSeasonPeriod)}</span>
                                    {preSeasonPeriod?.min_nights && <span className="font-label text-[10px] tracking-widest text-on-surface-variant font-bold mt-1 uppercase">{t('pricesPage.minNights', { count: preSeasonPeriod.min_nights, defaultValue: `Min. ${preSeasonPeriod.min_nights} nights` })}</span>}
                                </div>
                            </div>
                            <div className="bg-primary/5 text-primary rounded-xl p-1 shadow-[0_10px_40px_rgba(27,28,25,0.02)]">
                                {groupedPrices.map((ap: FormattedApartmentPrice, idx: number) => (
                                    <div key={`pre-${ap.apartment_id}`} className={`bg-white rounded-lg p-6 flex justify-between items-center ${idx < groupedPrices.length - 1 ? 'mb-1' : ''}`}>
                                        <div>
                                            <h4 className="font-body font-bold text-primary">{ap.apartment_name}</h4>
                                            <p className="text-xs text-on-surface-variant font-body">{t('pricesPage.table.guests')}</p>
                                        </div>
                                        <span className="font-headline text-xl text-primary">{getPrice(ap, preSeasonPeriod?.id)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        )}

                        {/* Post Season */}
                        {postSeasonPeriod && (
                        <div className="space-y-8">
                            <div className="flex items-end justify-between border-b border-surface-container-highest pb-4">
                                <h2 className="text-3xl font-headline text-primary">{t(postSeasonPeriod.name_key)}</h2>
                                <div className="flex flex-col items-end">
                                    <span className="font-label text-xs tracking-widest text-secondary font-bold">{getPeriodStr(postSeasonPeriod)}</span>
                                    {postSeasonPeriod?.min_nights && <span className="font-label text-[10px] tracking-widest text-on-surface-variant font-bold mt-1 uppercase">{t('pricesPage.minNights', { count: postSeasonPeriod.min_nights, defaultValue: `Min. ${postSeasonPeriod.min_nights} nights` })}</span>}
                                </div>
                            </div>
                            <div className="bg-primary/5 text-primary rounded-xl p-1 shadow-[0_10px_40px_rgba(27,28,25,0.02)]">
                                {groupedPrices.map((ap: FormattedApartmentPrice, idx: number) => (
                                    <div key={`post-${ap.apartment_id}`} className={`bg-white rounded-lg p-6 flex justify-between items-center ${idx < groupedPrices.length - 1 ? 'mb-1' : ''}`}>
                                        <div>
                                            <h4 className="font-body font-bold text-primary">{ap.apartment_name}</h4>
                                            <p className="text-xs text-on-surface-variant font-body">{t('pricesPage.table.guests')}</p>
                                        </div>
                                        <span className="font-headline text-xl text-primary">{getPrice(ap, postSeasonPeriod?.id)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        )}
                    </section>
                </div>

                {/* Additional Information Section */}
                <section className="mt-32 grid grid-cols-1 md:grid-cols-3 gap-12">
                    <div className="space-y-4">
                        <span className="material-symbols-outlined material-symbols-rounded text-secondary block text-3xl leading-none">info</span>
                        <h3 className="text-xl font-headline text-primary">{t('pricesPage.info.duration.title')}</h3>
                        <p className="text-sm text-on-surface-variant leading-relaxed">{t('pricesPage.info.duration.desc')}</p>
                    </div>
                    <div className="space-y-4">
                        <span className="material-symbols-outlined material-symbols-rounded text-secondary block text-3xl leading-none">check_circle</span>
                        <h3 className="text-xl font-headline text-primary">{t('pricesPage.info.services.title')}</h3>
                        <p className="text-sm text-on-surface-variant leading-relaxed">{t('pricesPage.info.services.desc')}</p>
                    </div>
                    <div className="space-y-4">
                        <span className="material-symbols-outlined material-symbols-rounded text-secondary block text-3xl leading-none">credit_card</span>
                        <h3 className="text-xl font-headline text-primary">{t('pricesPage.info.payment.title')}</h3>
                        <p className="text-sm text-on-surface-variant leading-relaxed">{t('pricesPage.info.payment.desc')}</p>
                    </div>
                </section>

                {/* Visual Anchor CTA */}
                <div className="mt-32 rounded-3xl overflow-hidden h-[400px] relative shadow-2xl">
                    <img alt="Luxury outdoor lounge at sunset" className="w-full h-full object-cover" src="https://lh3.googleusercontent.com/aida-public/AB6AXuDvlrf59HzYHNcOwvBohy_t3y1pNtYyqGL4bvQS4Dyl02_5CIaXHIZgzp2aXv7K4DE4Gvgl50W-kqagfRu9KkaIq9aU5VrOS1ajadmVIGZ1Jrys2SAAp17FQz-chEQpyrFcA9ss19tbK-JKBvnrmefteewrG1kM1oK0v22WjDhva_wTEf4oOgzJRrgYPG3ZRQr6hq9mz_e-N3TXouAwO1_VJ1t37Y4T3mm4STJmvj3-BHg1859ho0qOqELsiH7-2ZwtdNFf31RLn7U"/>
                    <div className="absolute inset-0 bg-primary/20 flex flex-col items-center justify-center text-center p-8 backdrop-blur-[2px]">
                        <h2 className="text-white text-3xl md:text-4xl lg:text-5xl font-headline mb-8">{t('pricesPage.cta.title')}</h2>
                        <button onClick={() => window.location.href='/booking'} className="bg-white text-primary px-10 py-4 rounded-full font-bold uppercase tracking-widest text-xs hover:bg-secondary hover:text-white transition-all shadow-xl">
                            {t('pricesPage.cta.button') || 'CHECK AVAILABILITY'}
                        </button>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default PricesPage; 